import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight } from '@/components/ui/icons';
import { LiecButton } from '@/components/ui/liec-components';
import { cn } from '@/lib/utils';
import { useResponsive, getFocusClasses, getTransitionClasses, getTouchAccessibilityClasses } from '@/lib/responsive-utils';
import type { HeroCarouselProps } from '@/types/hero';

export const HeroCarousel: React.FC<HeroCarouselProps> = ({
  slides,
  autoPlayInterval = 5000,
  className,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const { isMobile } = useResponsive();

  // Auto-advance functionality
  useEffect(() => {
    if (!isAutoPlaying || isHovered || slides.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, autoPlayInterval);

    return () => clearInterval(interval);
  }, [isAutoPlaying, isHovered, slides.length, autoPlayInterval]);

  // Manual navigation handlers
  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
    // Reset auto-play timer when manually navigating
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 1000);
  }, []);

  const goToPrevious = useCallback(() => {
    const prevIndex = currentSlide === 0 ? slides.length - 1 : currentSlide - 1;
    goToSlide(prevIndex);
  }, [currentSlide, slides.length, goToSlide]);

  const goToNext = useCallback(() => {
    const nextIndex = (currentSlide + 1) % slides.length;
    goToSlide(nextIndex);
  }, [currentSlide, slides.length, goToSlide]);

  // Touch/swipe handlers for mobile
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;

    if (isLeftSwipe) {
      goToNext();
    } else if (isRightSwipe) {
      goToPrevious();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        goToNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext]);

  if (!slides || slides.length === 0) {
    return null;
  }

  const currentSlideData = slides[currentSlide];

  return (
    <section
      className={cn(
        'relative w-full overflow-hidden bg-liec-neutral-900',
        'h-[500px] sm:h-[600px] md:h-[700px] lg:h-screen',
        'min-h-[400px] sm:min-h-[500px] md:min-h-[600px]',
        getTouchAccessibilityClasses(),
        className
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      aria-label="Hero carousel"
      role="region"
    >
      {/* Background Images with Animation */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlideData.id}
          className="absolute inset-0 z-0"
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.8, ease: 'easeInOut' }}
        >
          <div
            className="h-full w-full bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${currentSlideData.backgroundImage})`,
            }}
          />
          {/* Responsive overlay for better text readability */}
          <div className="absolute inset-0 bg-black/60 sm:bg-black/50 md:bg-black/40" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col justify-end md:flex-row md:items-center pb-4 md:mb-0">

        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-1">
          <div className="max-w-4xl lg:max-w-5xl xl:max-w-3xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={`content-${currentSlideData.id}`}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -30 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="text-white text-center sm:text-left"
              >
                <h1 className={cn(
                  'font-bold leading-tight text-white mb-3 sm:mb-6',
                  'text-2xl sm:text-2xl md:text-4xl lg:text-4xl xl:text-6xl 2xl:text-6xl'
                )}>
                  {currentSlideData.title}
                </h1>
                <p className={cn(
                  'leading-relaxed text-white/90 mb-6 sm:mb-8 max-w-2xl',
                  'text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl'
                )}>
                  {currentSlideData.description}
                </p>
                <LiecButton

                  size={isMobile ? "sm" : "lg"}
                  onClick={() => {
                    if (currentSlideData.ctaLink.startsWith('#')) {
                      // Smooth scroll to section
                      const element = document.querySelector(currentSlideData.ctaLink);
                      element?.scrollIntoView({ behavior: 'smooth' });
                    } else {
                      // External link
                      window.open(currentSlideData.ctaLink, '_blank', 'noopener,noreferrer');
                    }
                  }}
                  className={cn(
                    'transform transition-transform hover:scale-105',
                    isMobile ? 'w-full text-sm' : 'w-auto text-base'
                  )}
                >
                  {currentSlideData.ctaText}
                </LiecButton>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      {slides.length > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className={cn(
              'absolute top-1/2 z-20 -translate-y-1/2  bg-white/20 text-white backdrop-blur-sm',
              'hover:bg-white/30 hover:scale-110',
              getTransitionClasses(),
              getFocusClasses(),
              'left-2 sm:left-4 md:left-6 lg:left-8',
              'p-2 sm:p-3',
              'w-10 h-10 sm:w-12 sm:h-12'
            )}
            aria-label="Previous slide"
          >
            <ArrowLeft size={isMobile ? 16 : 20} className="sm:w-6 sm:h-6" />
          </button>
          <button
            onClick={goToNext}
            className={cn(
              'absolute top-1/2 z-20 -translate-y-1/2  bg-white/20 text-white backdrop-blur-sm',
              'hover:bg-white/30 hover:scale-110',
              getTransitionClasses(),
              getFocusClasses(),
              'right-2 sm:right-4 md:right-6 lg:right-8',
              'p-2 sm:p-3',
              'w-10 h-10 sm:w-12 sm:h-12'
            )}
            aria-label="Next slide"
          >
            <ArrowRight size={isMobile ? 16 : 20} className="sm:w-6 sm:h-6" />
          </button>
        </>
      )}

      {/* Slide Indicators 
      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 space-x-1 sm:bottom-6 md:bottom-8 sm:space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={cn(
                'rounded-full transition-all duration-300',
                getFocusClasses(),
                'h-1.5 w-4 sm:h-2 sm:w-6 md:w-8',
                index === currentSlide
                  ? 'bg-white'
                  : 'bg-white/40 hover:bg-white/60'
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
*/}
      {/* Progress Bar */}
      {isAutoPlaying && !isHovered && slides.length > 1 && (
        <div className="absolute bottom-0 left-0 z-20 h-1 w-full bg-white/20">
          <motion.div
            className="h-full bg-liec-primary-500"
            initial={{ width: '0%' }}
            animate={{ width: '100%' }}
            transition={{ duration: autoPlayInterval / 1000, ease: 'linear' }}
            key={currentSlide}
          />
        </div>
      )}

    </section>
  );
};